import {
  collection,
  doc,
  DocumentReference,
  onSnapshot,
  query,
  Query,
  QueryDocumentSnapshot,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { UpdateParams, UpdateResult } from "react-admin";
import { baseUrl as serverBaseUrl, getAuthenticatedServerClient, schemas } from "../api/server.api";
import { getDefaultUserGrantToggles, restrictAccessToDrivingSchoolIds } from "../backoffice.access_control";
import { drivingSchoolsProvider } from "./drivingSchoolsProvider";
import { firestore } from "../firebase";
import type { DrivingLicenseClass } from "../model/DrivingLicenseClass.js";
import { FeatureTogglesForInstructors } from "../model/FeatureToggles";
import { PostalAddress } from "../model/PostalAddress";
import { FirestoreProvider } from "./FirestoreProvider";
import { DateTime } from "luxon";
import { fromFirestore } from "../backoffice.utils";
import { isUndefined } from "../utils/filters";
import _isEqual from "lodash/isEqual";
import { updateApplied } from "../utils/updateApplied";

export interface Instructor {
  id: string;
  autovioUserId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  name: string;
  avatarUrl?: string;
  hubspotContactId?: string;
  applicationFeePercentage?: number;
  postalAddress?: Partial<PostalAddress>;
  drivingSchoolId: string;
  isDrivingSchoolManager: boolean;
  instructing: {
    drivingLicenseClasses: Array<DrivingLicenseClass>;
  };
  studentIds: Array<string>;
  assignedBranches: Array<string>;
  featureToggles: FeatureTogglesForInstructors;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  roles: Array<"instructor" | "drivingSchoolManager" | string>;
  grants: {
    [key: string]: boolean;
  };
  _sortOrder: string;
  startDate?: DateTime;
  endDate?: DateTime;
  isActive?: boolean;
  vogelId?: string;
}

export interface InstructorDocumentData {
  autovioUserId: string;
  email?: string;
  hubspotContactId?: string;
  applicationFeePercentage?: number;
  publicProfile: {
    firstName?: string;
    lastName?: string;
  };
  phoneNumber?: string;
  postalAddress?: Partial<PostalAddress>;
  drivingSchoolUid: string;
  instructing?: {
    drivingLicenseClasses?: Array<DrivingLicenseClass>;
    startDate?: Timestamp;
    endDate?: Timestamp;
  };
  myStudents: Array<string>;
  assignedBranches?: Array<string>;
  roles?: Array<string>;
  featureToggles?: FeatureTogglesForInstructors;
  grants?: Array<string>;
  vogelId?: string;
}

class InstructorProvider extends FirestoreProvider<Instructor> {
  private instructorsByAutovioUserId: Map<string, Instructor> = new Map();
  querySingle(id: string): [DocumentReference | Query, (record: Instructor) => void] {
    return [
      doc(firestore, `/users/${id}`),
      (instructor) => {
        if (
          restrictAccessToDrivingSchoolIds &&
          !restrictAccessToDrivingSchoolIds.includes(instructor.drivingSchoolId)
        ) {
          throw new Error(`Access to instructor ${id} denied`);
        }
      },
    ];
  }

  queryAll(): Query {
    return query(
      collection(firestore, `/${this.collection}`),
      restrictAccessToDrivingSchoolIds?.length === 1
        ? where("drivingSchoolUid", "==", restrictAccessToDrivingSchoolIds[0])
        : restrictAccessToDrivingSchoolIds
          ? where("drivingSchoolUid", "in", restrictAccessToDrivingSchoolIds)
          : where("drivingSchoolUid", "!=", ""),
    );
  }

  fromFirestore(snapshot: QueryDocumentSnapshot<InstructorDocumentData>): Instructor {
    const data = snapshot.data();
    const firstName = data.publicProfile.firstName ?? "???";
    const lastName = data.publicProfile.lastName ?? "???";
    const roles = data.roles ?? [];
    const isDrivingSchoolManager = roles.includes("drivingSchoolManager");
    const grants: { [key: string]: boolean } = {
      // Role-based defaults (reflecting effective permissions from mapRolesToGrants) ...
      ...getDefaultUserGrantToggles(
        roles as Role[],
        drivingSchoolsProvider.getOneFromCache(data.drivingSchoolUid)!.customizations,
      ),
    };
    // Explicit Firestore grants override role-based defaults ...
    for (const grant of data.grants ?? []) {
      if (grant.startsWith("!")) {
        grants[grant.substring(1)] = false;
      } else {
        grants[grant] = true;
      }
    }
    const { id } = snapshot;
    const name = `${firstName} ${lastName}`;
    const endDate = fromFirestore(data.instructing?.endDate);
    const startDate = fromFirestore(data.instructing?.startDate);
    const instructor: Instructor = {
      id,
      autovioUserId: data.autovioUserId,
      email: data.email,
      hubspotContactId: data.hubspotContactId,
      firstName,
      lastName,
      phoneNumber: data.phoneNumber,
      name,
      avatarUrl: `${serverBaseUrl}/user/${id}/avatar`,
      drivingSchoolId: data.drivingSchoolUid,
      instructing: {
        drivingLicenseClasses: data.instructing?.drivingLicenseClasses ?? [],
      },
      isDrivingSchoolManager,
      studentIds: data.myStudents ?? [],
      assignedBranches: data.assignedBranches ?? [],
      postalAddress: data.postalAddress,
      featureToggles: {
        ..._defaultFeatureToggles,
        ...data.featureToggles,
      },
      grants,
      roles: data.roles ?? [],
      // Sort all driving school managers before normal instructors ...
      _sortOrder: `${isDrivingSchoolManager ? 1 : 2} ${name}`,
      startDate,
      endDate,
      isActive: this._isInstructorActive(startDate, endDate),
      vogelId: data.vogelId,
    };
    this.instructorsByAutovioUserId.set(instructor.autovioUserId, instructor);
    return instructor;
  }

  getOneFromCache(id: string): Instructor | undefined {
    return this.instructorsByAutovioUserId.get(id) ?? super.getOneFromCache(id);
  }

  async getByDrivingSchoolId(drivingSchoolId: string): Promise<Array<Instructor>> {
    const { records } = await this.snapshot();
    return records.filter((it) => it.drivingSchoolId === drivingSchoolId);
  }

  async update(
    resource: string,
    { id, previousData, data }: UpdateParams<Instructor>,
  ): Promise<UpdateResult<Instructor>> {
    const delta = Object.fromEntries(
      Object.entries(data).filter(([k, v]) => !_isEqual(v, (previousData as any)[k])),
    ) as Partial<Instructor>;
    if (delta.grants) {
      delta.grants = Object.fromEntries(
        Object.entries(delta.grants).filter(([k, v]) => v !== !!previousData.grants?.[k]),
      ) as Instructor["grants"];
      if (Object.keys(delta.grants).length === 0) {
        delete delta.grants;
      }
    }
    if (Object.keys(delta).length === 0) {
      return { data: previousData };
    }
    let unsubscribe: undefined | (() => void);
    const updateReceived = new Promise<void>((resolve) => {
      unsubscribe = this.onUpdate(previousData, (instructor) => {
        if (instructorUpdateApplied(delta, instructor)) {
          if (unsubscribe) {
            unsubscribe();
            unsubscribe = undefined;
          }
          resolve();
        }
      });
    });
    try {
      const SERVER_PROPERTIES = [
        "grants",
        "firstName",
        "lastName",
        "phoneNumber",
        "instructing",
        "startDate",
        "endDate",
        "assignedBranches",
      ];
      if (SERVER_PROPERTIES.some((key) => key in delta)) {
        const serverClient = await getAuthenticatedServerClient();
        const { grants, firstName, lastName, phoneNumber, instructing, startDate, endDate, assignedBranches } = delta;
        if (grants) {
          const updateDto = schemas.UpdateUserGrantsDto.parse({ delta: grantsToArray(grants) });
          await serverClient.updateGrants(updateDto, { params: { id } });
        }
        if (
          firstName ||
          lastName ||
          phoneNumber ||
          instructing?.drivingLicenseClasses ||
          startDate ||
          endDate ||
          assignedBranches
        ) {
          const updateDto = schemas.UpdateInstructorDto.parse({
            ...(firstName ? { firstName } : {}),
            ...(lastName ? { lastName } : {}),
            ...(phoneNumber ? { phoneNumber } : {}),
            ...(instructing?.drivingLicenseClasses ? { drivingLicenseClasses: instructing.drivingLicenseClasses } : {}),
            ...(startDate ? { startDate: toUtcDateIso(startDate) } : {}),
            ...(endDate ? { endDate: toUtcDateIso(endDate) } : {}),
            ...(assignedBranches ? { assignedBranches } : {}),
          });
          await serverClient.updateInstructor(updateDto, { params: { id } });
        }
      }
      const { featureToggles, ...rest } = Object.fromEntries(
        Object.entries(delta).filter(([key, value]) => !isUndefined(value) && !SERVER_PROPERTIES.includes(key)),
      );
      if (Object.keys(rest).length > 0) {
        throw new Error(`Unexpected data: ${JSON.stringify(rest)}`);
      }
      if (Object.keys(featureToggles ?? {}).length > 0) {
        const firestoreUpdateData = { featureToggles };
        console.info(`Updating Firestore document users/${id} ...`, firestoreUpdateData);
        await setDoc(doc(firestore, `users/${id}`), firestoreUpdateData, { merge: true });
      }
      await updateReceived;
      return super.getOne(resource, { id });
    } finally {
      try {
        unsubscribe?.();
      } catch {
        // Ignored.
      }
    }
  }

  _isInstructorActive(startDate?: DateTime, endDate?: DateTime): boolean {
    if (!startDate && !endDate) {
      return true;
    }
    const now = DateTime.now();
    const isAfterStart = !startDate || startDate.toMillis() < now.toMillis();
    const isBeforeEnd = !endDate || endDate.toMillis() > now.toMillis();
    return isAfterStart && isBeforeEnd;
  }

  onCreate(instructor: { id: string }, listener: (instructor: Instructor) => void): () => void {
    const unsubscribe = this.onUpdate(instructor, listener);
    const alreadyReceived = this._recordsById?.get(instructor.id);
    if (alreadyReceived) {
      listener(alreadyReceived);
    }
    return unsubscribe;
  }

  onUpdate(instructor: { id: string }, listener: (instructor: Instructor) => void): () => void {
    const unsubscribe = onSnapshot(doc(firestore, `users/${instructor.id}`), (snapshot) => {
      if (!snapshot.exists()) {
        return;
      }
      const updatedInstructor = this.fromFirestore(snapshot as QueryDocumentSnapshot<InstructorDocumentData>);
      if (this._recordsById) {
        this._recordsById.set(updatedInstructor.id, updatedInstructor);
        this._recordsById.set(updatedInstructor.autovioUserId, updatedInstructor);
      }
      this.instructorsByAutovioUserId.set(updatedInstructor.autovioUserId, updatedInstructor);
      listener(updatedInstructor);
    });
    return unsubscribe;
  }
}

const _defaultFeatureToggles: FeatureTogglesForInstructors = {
  lab: false,
};

export const instructorsProvider = new InstructorProvider("Instructor", "users");

export function grantsToArray(grants: { [grant: string]: boolean }): Array<string> {
  return Object.entries(grants).map(([key, value]) => (value ? key : `!${key}`));
}

function instructorUpdateApplied(delta: Partial<Instructor>, instructor: Instructor): boolean {
  const { startDate, endDate, assignedBranches, ...rest } = delta;
  return (
    (!startDate || isSameCalendarDate(startDate, instructor.startDate)) &&
    (!endDate || isSameCalendarDate(endDate, instructor.endDate)) &&
    (!assignedBranches || isSameUnorderedList(assignedBranches, instructor.assignedBranches)) &&
    updateApplied(rest, instructor)
  );
}

function isSameUnorderedList(expected: Array<string>, actual: Array<string>): boolean {
  if (expected.length !== actual.length) {
    return false;
  }
  const sortedExpected = [...expected].sort();
  const sortedActual = [...actual].sort();
  return sortedExpected.every((it, i) => it === sortedActual[i]);
}

function isSameCalendarDate(expected: DateTime, actual?: DateTime): boolean {
  return Boolean(actual?.isValid && expected.toISODate() === actual.toISODate());
}

function toUtcDateIso(date: DateTime): string {
  const isoDate = date.toISODate();
  if (!isoDate) {
    throw new Error(`Invalid DateTime: ${date.invalidExplanation ?? date.invalidReason}`);
  }
  return DateTime.fromISO(isoDate, { zone: "utc" }).toISO()!;
}
